(() => {
  if (customElements.get('luxen-setup-builder')) return;

  const Core = () => window.LuxenSetupBuilderCore;
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character]);
  const idSafe = (value) => String(value || '').replace(/[^a-zA-Z0-9_-]/g, '-');

  class LuxenSetupBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised === 'true') return;
      this.dataset.initialised = 'true';
      const configNode = this.querySelector('[data-setup-config]');
      this.dialog = this.querySelector('[data-setup-dialog]');
      this.view = this.querySelector('[data-setup-view]');
      this.progress = this.querySelector('[data-setup-progress]');
      this.announcement = this.querySelector('[data-setup-announcement]');
      if (!configNode || !this.dialog || !Core()) return;
      try { this.config = Core().normalise(JSON.parse(configNode.textContent)); } catch (error) { return; }
      this.bundleComponentCache = new Map();
      this.bundleComponentRequests = new Map();
      this.bundleComponentErrors = new Set();
      this.trustStories = [];
      this.trustStoriesLoading = false;
      this.trustStoriesLoaded = false;
      this.state = this.initialState();
      this.lastTrigger = null;
      this.boundDocumentClick = this.onDocumentClick.bind(this);
      document.addEventListener('click', this.boundDocumentClick);
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.dialog.addEventListener('close', () => this.afterClose());
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        if (this.querySelector('.ugc-modal:not([hidden])')) return;
        this.close();
      });
      document.dispatchEvent(new CustomEvent('setup_builder_ready'));
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.boundDocumentClick);
    }

    initialState() {
      return { screen: 'start', mode: 'recommend', ownSelections: {}, selectedRooms: [], needsByRoom: {}, selections: {}, addingRoom: false, presetNeedKey: null, entry: {}, recommendedBundleId: null, useRecommendedBundle: false, bundleVariantId: null, filters: {} };
    }

    onDocumentClick(event) {
      const trigger = event.target.closest('[data-luxen-setup-builder-trigger],a[href$="#build-your-setup"]');
      if (!trigger || !document.documentElement.contains(trigger)) return;
      event.preventDefault();
      this.open(trigger);
    }

    open(trigger) {
      this.lastTrigger = trigger;
      this.state = this.initialState();
      this.state.entry = {
        point: trigger?.dataset.setupEntry || (trigger?.matches('a[href$="#build-your-setup"]') ? 'navigation' : 'unknown'),
        productId: trigger?.dataset.setupProductId || null,
        productHandle: trigger?.dataset.setupProductHandle || null,
        variantId: trigger?.dataset.setupVariantId || null,
        productType: trigger?.dataset.setupProductType || null
      };
      this.applyTriggerPreset(trigger);
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('setup-builder-open');
      this.render();
      requestAnimationFrame(() => this.dialog.querySelector('[data-setup-close]')?.focus());
      this.emit('setup_builder_open', { entry_point: this.state.entry.point });
    }

    applyTriggerPreset(trigger) {
      const roomKey = Core().keys(trigger?.dataset.setupRoomKey)[0];
      const needKey = Core().keys(trigger?.dataset.setupNeedKey)[0];
      const room = this.room(roomKey);
      const need = this.need(needKey);
      this.state.presetNeedKey = need?.key || null;
      if (room) {
        if (!this.state.selectedRooms.includes(roomKey)) this.state.selectedRooms.push(roomKey);
        this.applyPresetNeedToRoom(roomKey);
      } else if (need) {
        this.state.selectedRooms.forEach((selectedRoomKey) => this.applyPresetNeedToRoom(selectedRoomKey));
      }
      this.state.addingRoom = false;
    }

    applyPresetNeedToRoom(roomKey) {
      const room = this.room(roomKey);
      const needKey = this.state.presetNeedKey;
      if (!room || !needKey || !this.need(needKey)) return;
      const allowedNeeds = this.availableNeeds(room).map((item) => item.key);
      if (!allowedNeeds.includes(needKey)) return;
      if (!this.state.needsByRoom[roomKey]) this.state.needsByRoom[roomKey] = [];
      if (!this.state.needsByRoom[roomKey].includes(needKey)) this.state.needsByRoom[roomKey].push(needKey);
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    afterClose() {
      this.view.querySelectorAll('[data-ugc-carousel]').forEach((section) => section.ugcCleanup?.());
      document.documentElement.classList.remove('setup-builder-open');
      this.emit('setup_builder_close', this.metrics());
      this.lastTrigger?.focus();
    }

    emit(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(name, { detail }));
    }

    metrics() {
      const pricing = this.pricingResult();
      const bundleQuantity = pricing.match && !pricing.match.unavailable ? ['fan', 'purifier', 'vacuum', 'filter'].reduce((sum, key) => sum + Number(pricing.match.offer[key] || 0), 0) : null;
      return { entry_point: this.state.entry.point, number_of_rooms: this.state.selectedRooms.length, number_of_products: this.state.mode === 'build-own' ? this.ownProductCount() : bundleQuantity ?? (this.setupUnits().length + this.addedFilterCount()), setup_value: pricing.total, pricing_mode: this.state.mode === 'build-own' ? 'build_own' : pricing.match && !pricing.match.unavailable ? 'fixed_bundle' : 'individual', offer: pricing.match && !pricing.match.unavailable ? pricing.match.offer.label : null };
    }

    money(cents) {
      return new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency }).format(Number(cents || 0) / 100);
    }

    room(key) { return this.config.rooms.find((room) => room.key === key); }
    need(key) { return this.config.needs.find((need) => need.key === key); }
    activeRooms() { return this.state.selectedRooms.filter((key) => this.state.selections[key] && !this.state.selections[key].removed && Core().resolveSelection(this.config, this.state.selections[key])); }

    productRole(resolved) {
      if (!resolved) return 'other';
      const configured = resolved.rule?.productRole;
      if (configured && configured !== 'other') return configured;
      return 'other';
    }

    selectionVariants(selection, resolved) {
      if (!selection || !resolved) return [];
      const ids = Array.isArray(selection.variantIds) && selection.variantIds.length ? selection.variantIds : [selection.variantId];
      return Array.from({ length: Number(selection.quantity || 1) }, (_, index) => Core().availableVariant(resolved.product, ids[index]) || resolved.variant);
    }

    setupUnits() {
      return this.activeRooms().flatMap((roomKey) => {
        const selection = this.state.selections[roomKey];
        const resolved = Core().resolveSelection(this.config, selection);
        return this.selectionVariants(selection, resolved).map((variant, unitIndex) => ({
          roomKey,
          unitIndex,
          role: this.productRole(resolved),
          rule: resolved.rule,
          product: resolved.product,
          variant
        }));
      });
    }

    ownSelection(item) {
      const stored = this.state.ownSelections[item.id] || {};
      const quantity = Number(stored.quantity || 0);
      const variantIds = Array.isArray(stored.variantIds) ? stored.variantIds : Array.from({ length: quantity }, () => stored.variantId);
      const variants = Array.from({ length: quantity }, (_, index) => Core().availableVariant(item.product, variantIds[index]));
      return { quantity, variants, variant: variants[0] || Core().availableVariant(item.product) };
    }

    ownProductCount() {
      return (this.config.buildOwnProducts || []).reduce((total, item) => total + this.ownSelection(item).quantity, 0);
    }

    ownApplianceCount() {
      return (this.config.buildOwnProducts || []).reduce((total, item) => total + (['fan', 'purifier', 'vacuum'].includes(item.role) ? this.ownSelection(item).quantity : 0), 0);
    }

    ownTotal() {
      return (this.config.buildOwnProducts || []).reduce((total, item) => {
        const selection = this.ownSelection(item);
        return total + selection.variants.reduce((sum, variant) => sum + Number(variant?.price || 0), 0);
      }, 0);
    }

    changeOwnQuantity(productId, delta) {
      const item = this.config.buildOwnProducts.find((product) => product.id === productId);
      if (!item) return;
      const selection = this.state.ownSelections[item.id] || { quantity: 0, variantId: Core().availableVariant(item.product)?.id || null };
      const roleCount = this.config.buildOwnProducts.reduce((total, product) => total + (product.role === item.role ? Number(this.state.ownSelections[product.id]?.quantity || 0) : 0), 0);
      const roleCap = item.role === 'filter' ? item.maxQuantity : Math.min(item.maxQuantity, Number(this.config.pricing?.caps?.[item.role] || item.maxQuantity));
      const nextQuantity = Math.max(0, Math.min(item.maxQuantity, selection.quantity + delta));
      if (delta > 0 && item.role !== 'filter' && roleCount >= Number(this.config.pricing?.caps?.[item.role] || item.maxQuantity)) return;
      selection.quantity = Math.min(nextQuantity, roleCap);
      if (!Array.isArray(selection.variantIds)) selection.variantIds = Array.from({ length: Math.max(0, selection.quantity - delta) }, () => selection.variantId || Core().availableVariant(item.product)?.id);
      if (selection.quantity > selection.variantIds.length) {
        const defaultVariantId = Core().availableVariant(item.product)?.id || null;
        while (selection.variantIds.length < selection.quantity) selection.variantIds.push(defaultVariantId);
      } else {
        selection.variantIds.length = selection.quantity;
      }
      this.state.ownSelections[item.id] = selection;
      this.render();
    }

    changeOwnVariant(productId, unitIndex, variantId) {
      const item = this.config.buildOwnProducts.find((product) => product.id === productId);
      if (!item) return;
      const selection = this.state.ownSelections[item.id];
      if (!selection || unitIndex >= selection.quantity) return;
      selection.variantIds[unitIndex] = Number(variantId);
      this.render();
    }

    ownCartItems(bundleId) {
      return this.config.buildOwnProducts.flatMap((item) => {
        const selection = this.ownSelection(item);
        if (!selection.quantity) return [];
        const quantitiesByVariant = new Map();
        selection.variants.forEach((variant) => {
          if (variant) quantitiesByVariant.set(variant.id, (quantitiesByVariant.get(variant.id) || 0) + 1);
        });
        return [...quantitiesByVariant].map(([variantId, quantity]) => ({
          id: variantId,
          quantity,
          properties: {
          _luxen_setup_id: bundleId,
            _luxen_setup_name: 'Build Your Own Bundle',
            _luxen_appliance_role: item.role,
            ...(item.role === 'fan' || item.role === 'purifier' || item.role === 'vacuum' ? { _luxen_pricing_eligible: 'true' } : {}),
            _setup_entry: this.state.entry.point || 'unknown'
          }
        }));
      });
    }

    applianceCounts() {
      const counts = { fan: 0, purifier: 0, vacuum: 0, filter: this.addedFilterCount() };
      this.setupUnits().forEach((unit) => { if (Object.hasOwn(counts, unit.role)) counts[unit.role] += 1; });
      return counts;
    }

    variantOption(product, variant, pattern) {
      if (!product || !variant) return null;
      const optionIndex = (product.options || []).findIndex((option) => pattern.test(String(option.name || '').toLowerCase()));
      return optionIndex >= 0 ? variant.options?.[optionIndex] : null;
    }

    unitColour(unit) {
      if (!unit) return null;
      return this.variantOption(unit.product, unit.variant, /colou?r/) || (unit.variant.title !== 'Default Title' ? unit.variant.options?.[0] : null);
    }

    bundleRecommendation() {
      const selectedRooms = new Set(this.state.selectedRooms);
      const selectedNeeds = new Set([...selectedRooms].flatMap((roomKey) => this.state.needsByRoom[roomKey] || []));
      const selectedRoomCount = selectedRooms.size;
      const offers = (this.config.fixedBundles || [])
        .filter((offer) => offer.featured !== false && offer.product?.variants?.length)
        .map((offer) => {
          const matchedRooms = offer.roomKeys.length
            ? offer.roomKeys.filter((key) => selectedRooms.has(key))
            : [...selectedRooms];
          const roomMatches = offer.roomKeys.length ? matchedRooms.length : 0;
          const needMatches = offer.needKeys.filter((key) => selectedNeeds.has(key)).length;
          const roomCountMatches = selectedRoomCount >= Number(offer.minRooms || 0) && (!Number(offer.maxRooms || 0) || selectedRoomCount <= Number(offer.maxRooms || 0));
          const roomMatchesConfig = !offer.roomKeys.length || roomMatches > 0;
          const needMatchesConfig = offer.matchMode === 'fallback'
            || !offer.needKeys.length
            || (offer.matchMode === 'all' ? needMatches === offer.needKeys.length : needMatches > 0);
          const eligible = roomCountMatches && roomMatchesConfig && needMatchesConfig;
          return { offer, eligible, score: needMatches + roomMatches };
        })
        .filter((candidate) => candidate.eligible);
      const explicitOffers = offers.filter(({ offer }) => offer.matchMode !== 'fallback');
      const fallbackOffers = offers.filter(({ offer }) => offer.matchMode === 'fallback');

      return (explicitOffers.length ? explicitOffers : fallbackOffers)
        .sort((a, b) => b.offer.priority - a.offer.priority || b.score - a.score || a.offer.index - b.offer.index)[0]?.offer || null;
    }

    recommendedBundle() {
      return (this.config.fixedBundles || []).find((offer) => offer.id === this.state.recommendedBundleId) || null;
    }

    recommendedBundleVariant() {
      const offer = this.recommendedBundle();
      if (!offer) return null;
      return offer.product.variants.find((variant) => String(variant.id) === String(this.state.bundleVariantId)) || Core().availableVariant(offer.product) || offer.product.variants[0] || null;
    }

    bundleComponentTotal(variant) {
      const components = variant ? this.bundleComponentCache?.get(String(variant.id)) : null;
      return components?.reduce((total, component) => total + component.lineTotal, 0) || 0;
    }

    bundleProductCount(offer, variant) {
      const components = variant ? this.bundleComponentCache?.get(String(variant.id)) : null;
      if (components?.length) return components.reduce((total, component) => total + Number(component.quantity || 1), 0);
      return ['fan', 'purifier', 'vacuum', 'filter'].reduce((total, key) => total + Number(offer?.[key] || 0), 0) || 1;
    }

    bundleSeparateTotal(variant) {
      return this.bundleComponentTotal(variant) || Number(variant?.compareAtPrice || 0);
    }

    bundleVariant(offer, units) {
      const roleUnits = {
        fan: units.filter((unit) => unit.role === 'fan'),
        purifier: units.filter((unit) => unit.role === 'purifier'),
        vacuum: units.filter((unit) => unit.role === 'vacuum')
      };
      const optionValues = (offer.product.options || []).map((option) => {
        const name = String(option.name || '').trim().toLowerCase();
        let value = null;
        if (/fan\s*1.*colou?r/.test(name)) value = this.unitColour(roleUnits.fan[0]);
        else if (/fan\s*2.*colou?r/.test(name)) value = this.unitColour(roleUnits.fan[1]);
        else if (/^fan.*colou?r/.test(name)) {
          const colours = roleUnits.fan.map((unit) => this.unitColour(unit)).filter(Boolean);
          if (new Set(colours.map((colour) => colour.toLowerCase())).size > 1) return null;
          value = colours[0];
        } else if (/purifier.*colou?r/.test(name)) value = this.unitColour(roleUnits.purifier[0]);
        else if (/vacuum.*colou?r/.test(name)) value = this.unitColour(roleUnits.vacuum[0]);
        else if (option.values?.length === 1) value = option.values[0];
        if (!value) return null;
        return option.values?.find((candidate) => String(candidate).toLowerCase() === String(value).toLowerCase()) || value;
      });
      if (optionValues.some((value) => value == null)) return null;
      return (offer.product.variants || []).find((variant) => variant.options?.every((value, index) => String(value).toLowerCase() === String(optionValues[index]).toLowerCase())) || null;
    }

    fixedBundleMatch() {
      const counts = this.applianceCounts();
      const units = this.setupUnits();
      if (!units.length && !counts.filter) return null;
      if (units.some((unit) => !['fan', 'purifier', 'vacuum'].includes(unit.role))) return null;
      const exactOffers = (this.config.fixedBundles || []).filter((offer) => (offer.fan + offer.purifier + offer.vacuum + offer.filter > 0) && ['fan', 'purifier', 'vacuum', 'filter'].every((key) => Number(offer[key] || 0) === Number(counts[key] || 0)));
      if (!exactOffers.length || (counts.filter && this.addedFilterOptions().some((option) => this.filterSelection(option).purchase !== 'once'))) return null;
      const matches = exactOffers.map((offer) => ({ offer, variant: this.bundleVariant(offer, units) }))
        .sort((a, b) => Number(a.variant?.price ?? Infinity) - Number(b.variant?.price ?? Infinity) || b.offer.priority - a.offer.priority || a.offer.index - b.offer.index);
      return matches.find((match) => match.variant?.available) || { ...matches[0], unavailable: true };
    }

    pricingResult() {
      if (this.state.mode === 'build-own') {
        const total = this.ownTotal();
        return { regularTotal: total, total, saving: 0, label: null, match: null };
      }
      if (this.state.useRecommendedBundle) {
        const offer = this.recommendedBundle();
        const variant = this.recommendedBundleVariant();
        if (offer && variant) {
          const extraFilter = this.filterPrice();
          const total = Number(variant.price || 0) + extraFilter;
          const compareAt = this.bundleSeparateTotal(variant);
          return {
            regularTotal: (compareAt > variant.price ? compareAt : Number(variant.price || 0)) + extraFilter,
            total,
            saving: compareAt > variant.price ? compareAt - Number(variant.price || 0) : 0,
            label: offer.label || offer.product.title,
            match: { offer, variant, unavailable: !variant.available, recommended: true }
          };
        }
      }
      const regularTotal = Core().total(this.config, this.state.selectedRooms, this.state.selections) + this.filterPrice();
      const match = this.fixedBundleMatch();
      if (!match || match.unavailable) return { regularTotal, total: regularTotal, saving: 0, label: null, match };
      const total = Number(match.variant.price || 0);
      const compareAt = this.bundleSeparateTotal(match.variant);
      const saving = compareAt > total ? compareAt - total : 0;
      return { regularTotal: compareAt > total ? compareAt : regularTotal, total, saving, label: match.offer.label || match.offer.product.title, match };
    }

    onClick(event) {
      const control = event.target.closest('[data-setup-action]');
      if (event.target.closest('[data-setup-close]')) { this.close(); return; }
      if (event.target.closest('[data-setup-back]')) { this.back(); return; }
      if (!control) return;
      const action = control.dataset.setupAction;
      if (action === 'view-filter-options') event.preventDefault();
      const roomKey = control.dataset.roomKey;
      if (action === 'toggle-room') this.toggleRoom(roomKey);
      if (action === 'toggle-need') this.toggleNeed(roomKey, control.dataset.needKey);
      if (action === 'choose-recommend') { this.state.screen = 'rooms'; this.render(); }
      if (action === 'choose-build-own') { this.state.mode = 'build-own'; this.state.screen = 'own'; this.render(); }
      if (action === 'rooms-next') this.roomsNext();
      if (action === 'needs-next') this.createRecommendations();
      if (action === 'customise') { this.state.screen = 'customise'; this.render(); this.loadTrustStories(); }
      if (action === 'build-own') { this.state.useRecommendedBundle = false; this.state.screen = 'setup'; this.render(); }
      if (action === 'add-room') { this.state.addingRoom = true; this.state.screen = 'rooms'; this.render(); }
      if (action === 'switch-product') this.switchProduct(roomKey);
      if (action === 'switch-product-group') this.switchProductGroup(Core().keys(control.dataset.roomKeys));
      if (action === 'remove-product') this.removeProduct(roomKey);
      if (action === 'restore-product') this.restoreProduct(roomKey);
      if (action === 'quantity') this.changeQuantity(roomKey, Number(control.dataset.delta));
      if (action === 'own-quantity') this.changeOwnQuantity(control.dataset.ownProduct, Number(control.dataset.delta));
      if (action === 'toggle-filter') this.toggleFilter(control.dataset.setupFilterKey);
      if (action === 'view-filter-options') this.viewFilterOptions();
      if (action === 'trust-scroll') this.scrollTrustStories(Number(control.dataset.direction || 1));
      if (action === 'add-to-cart') this.addToCart(control);
      if (action === 'join-waitlist') this.openBundleWaitlist();
    }

    onChange(event) {
      const ownVariant = event.target.closest('[data-own-variant]');
      if (ownVariant) {
        this.changeOwnVariant(ownVariant.dataset.ownVariant, Number(ownVariant.dataset.ownUnit || 0), ownVariant.value);
        return;
      }
      const select = event.target.closest('[data-setup-variant]');
      const filterVariant = event.target.closest('[data-setup-filter-variant]');
      const filterPurchase = event.target.closest('[name="setup-filter-purchase"]');
      const filterPlan = event.target.closest('[data-setup-filter-plan]');
      const bundleOption = event.target.closest('[data-setup-bundle-option]');
      if (bundleOption) {
        const offer = this.recommendedBundle();
        const current = this.recommendedBundleVariant();
        if (!offer || !current) return;
        const values = [...current.options];
        values[Number(bundleOption.dataset.setupBundleOption)] = bundleOption.value;
        const variant = offer.product.variants.find((item) => item.options?.every((value, index) => String(value) === String(values[index])));
        this.state.bundleVariantId = variant?.id || current.id;
        this.render();
        this.loadBundleComponents();
        return;
      }
      if (filterVariant) {
        const option = this.filterOptions().find((item) => item.key === String(filterVariant.dataset.setupFilterKey));
        if (!option) return;
        this.filterSelection(option).variantId = Number(filterVariant.value);
        this.syncFilterPlan(option);
        this.render();
        return;
      }
      if (filterPurchase) {
        const option = this.filterOptions().find((item) => item.key === String(filterPurchase.dataset.setupFilterKey));
        if (!option) return;
        this.filterSelection(option).purchase = filterPurchase.value;
        this.syncFilterPlan(option);
        this.render();
        return;
      }
      if (filterPlan) {
        const option = this.filterOptions().find((item) => item.key === String(filterPlan.dataset.setupFilterKey));
        if (!option) return;
        this.filterSelection(option).planId = Number(filterPlan.value);
        this.render();
        return;
      }
      if (!select) return;
      const roomKeys = Core().keys(select.dataset.roomKeys || select.dataset.roomKey);
      roomKeys.forEach((key) => {
        const selection = this.state.selections[key];
        if (!selection) return;
        if (!Array.isArray(selection.variantIds)) selection.variantIds = Array.from({ length: selection.quantity }, () => selection.variantId);
        const unitIndex = select.dataset.unitIndex;
        if (unitIndex != null && unitIndex !== 'all') {
          selection.variantIds[Number(unitIndex)] = Number(select.value);
        } else {
          selection.variantIds = selection.variantIds.map(() => Number(select.value));
        }
        selection.variantId = selection.variantIds[0];
      });
      this.emit('setup_product_added', this.metrics());
      this.render();
    }

    toggleRoom(roomKey) {
      if (!this.room(roomKey)) return;
      const index = this.state.selectedRooms.indexOf(roomKey);
      if (index >= 0) {
        this.state.selectedRooms.splice(index, 1);
      } else {
        this.state.selectedRooms.push(roomKey);
        if (!this.state.needsByRoom[roomKey]) this.state.needsByRoom[roomKey] = [];
        this.applyPresetNeedToRoom(roomKey);
      }
      this.emit('setup_room_selected', { ...this.metrics(), room: roomKey, selected: index < 0 });
      this.render();
    }

    toggleNeed(roomKey, needKey) {
      const values = this.state.needsByRoom[roomKey] || (this.state.needsByRoom[roomKey] = []);
      const index = values.indexOf(needKey);
      if (index >= 0) values.splice(index, 1); else values.push(needKey);
      this.emit('setup_need_selected', { ...this.metrics(), room: roomKey, need: needKey, selected: index < 0 });
      this.render();
    }

    roomsNext() {
      if (!this.state.selectedRooms.length) return;
      this.state.screen = 'needs';
      this.render();
    }

    createRecommendations() {
      this.state.selectedRooms.forEach((roomKey) => {
        const rule = Core().recommendation(this.config, roomKey, this.state.needsByRoom[roomKey]);
        const current = this.state.selections[roomKey];
        this.state.selections[roomKey] = Core().selectionFor(rule, current?.ruleId === rule?.id ? current : null);
      });
      Object.keys(this.state.selections).forEach((key) => { if (!this.state.selectedRooms.includes(key)) delete this.state.selections[key]; });
      const recommendedBundle = this.bundleRecommendation();
      this.state.recommendedBundleId = recommendedBundle?.id || null;
      this.state.useRecommendedBundle = Boolean(recommendedBundle);
      this.state.bundleVariantId = (Core().availableVariant(recommendedBundle?.product) || (this.config.designMode ? recommendedBundle?.product?.variants?.[0] : null))?.id || null;
      this.state.screen = 'setup';
      this.state.addingRoom = false;
      this.emit('setup_recommendations_viewed', this.metrics());
      this.render();
      this.loadBundleComponents();
      this.loadTrustStories();
    }

    switchProduct(roomKey) {
      const selection = this.state.selections[roomKey];
      const resolved = Core().resolveSelection(this.config, selection);
      if (!selection || !resolved?.rule.alternative) return;
      selection.productChoice = selection.productChoice === 'alternative' ? 'product' : 'alternative';
      selection.variantId = Core().availableVariant(resolved.rule[selection.productChoice])?.id || null;
      selection.variantIds = Array.from({ length: selection.quantity }, () => selection.variantId);
      selection.removed = false;
      this.emit('setup_product_added', { ...this.metrics(), room: roomKey });
      this.render();
    }

    switchProductGroup(roomKeys) {
      let changed = false;
      roomKeys.forEach((roomKey) => {
        const selection = this.state.selections[roomKey];
        const resolved = Core().resolveSelection(this.config, selection);
        if (!selection || !resolved?.rule.alternative) return;
        selection.productChoice = selection.productChoice === 'alternative' ? 'product' : 'alternative';
        selection.variantId = Core().availableVariant(resolved.rule[selection.productChoice])?.id || null;
        selection.variantIds = Array.from({ length: selection.quantity }, () => selection.variantId);
        selection.removed = false;
        changed = true;
      });
      if (!changed) return;
      this.emit('setup_product_added', { ...this.metrics(), rooms: roomKeys });
      this.render();
    }

    removeProduct(roomKey) {
      if (!this.state.selections[roomKey]) return;
      this.state.selections[roomKey].removed = true;
      this.emit('setup_product_removed', { ...this.metrics(), room: roomKey });
      this.render();
    }

    restoreProduct(roomKey) {
      if (!this.state.selections[roomKey]) return;
      this.state.selections[roomKey].removed = false;
      this.emit('setup_product_added', { ...this.metrics(), room: roomKey });
      this.render();
    }

    changeQuantity(roomKey, delta) {
      const selection = this.state.selections[roomKey];
      if (!selection) return;
      const resolved = Core().resolveSelection(this.config, selection);
      const role = this.productRole(resolved);
      const cap = Number(this.config.pricing?.caps?.[role] || 10);
      const currentRoleQuantity = Number(this.applianceCounts()[role] || 0);
      if (delta > 0 && currentRoleQuantity >= cap) return;
      const nextQuantity = Math.max(1, Math.min(selection.quantity + delta, selection.quantity + Math.max(0, cap - currentRoleQuantity)));
      if (!Array.isArray(selection.variantIds)) selection.variantIds = Array.from({ length: selection.quantity }, () => selection.variantId);
      if (nextQuantity > selection.quantity) {
        while (selection.variantIds.length < nextQuantity) selection.variantIds.push(selection.variantIds[selection.variantIds.length - 1] || selection.variantId);
      } else {
        selection.variantIds = selection.variantIds.slice(0, nextQuantity);
      }
      selection.quantity = nextQuantity;
      selection.variantId = selection.variantIds[0];
      this.emit('setup_quantity_changed', { ...this.metrics(), room: roomKey, quantity: selection.quantity });
      this.render();
    }

    eligibleForFilter() {
      return this.filterOptions().length > 0;
    }

    recommendedBundleIncludesFilter() {
      if (!this.state.useRecommendedBundle) return false;
      const variant = this.recommendedBundleVariant();
      const components = variant ? this.bundleComponentCache?.get(String(variant.id)) : null;
      const filterIds = new Set(this.filterOptions().map((option) => String(option.product.id)));
      return Boolean(Number(this.recommendedBundle()?.filter || 0) > 0 || components?.some((component) => filterIds.has(String(component.productId))));
    }

    fallbackFilterProduct() {
      return this.config.filterProduct || null;
    }

    filterKey(option) {
      return String(option?.product?.id || '');
    }

    filterSelection(option) {
      const key = this.filterKey(option);
      if (!key) return { added: false, variantId: null, purchase: 'subscription', planId: null };
      this.state.filters[key] = this.state.filters[key] || { added: false, variantId: null, purchase: 'subscription', planId: null };
      return this.state.filters[key];
    }

    addFilterOption(options, product, sourceTitle) {
      if (!product?.variants?.length) return;
      const key = String(product.id);
      const existing = options.get(key);
      if (existing) {
        if (sourceTitle && !existing.sourceTitles.includes(sourceTitle)) existing.sourceTitles.push(sourceTitle);
        return;
      }
      options.set(key, { key, product, sourceTitles: sourceTitle ? [sourceTitle] : [] });
    }

    filterOptions() {
      const options = new Map();
      if (this.state.useRecommendedBundle) {
        const variant = this.recommendedBundleVariant();
        const components = variant ? this.bundleComponentCache?.get(String(variant.id)) : null;
        if (components?.length) {
          components.forEach((component) => this.addFilterOption(options, this.componentReplacementFilter(component), component.productTitle));
        } else {
          const offer = this.recommendedBundle();
          const fallbackByRole = (role) => this.bundleRoleProduct(offer, role)?.replacementFilter || this.fallbackFilterProduct();
          if (Number(offer?.fan || 0) > 0) this.addFilterOption(options, fallbackByRole('fan'), this.bundleRoleProduct(offer, 'fan')?.title || '3 in 1');
          if (Number(offer?.purifier || 0) > 0) this.addFilterOption(options, fallbackByRole('purifier'), this.bundleRoleProduct(offer, 'purifier')?.title || 'Purifier');
        }
      } else {
        this.setupUnits().forEach((unit) => {
          const resolved = Core().resolveSelection(this.config, this.state.selections[unit.roomKey]);
          const product = unit.product?.replacementFilter || (resolved?.rule.offerFilter ? this.fallbackFilterProduct() : null);
          this.addFilterOption(options, product, unit.product?.title);
        });
      }
      return [...options.values()];
    }

    componentReplacementFilter(component) {
      if (component?.replacementFilter) return component.replacementFilter;
      const candidates = this.config.buildOwnProducts?.map((item) => item.product).filter(Boolean) || [];
      const matched = candidates.find((product) => String(product.id) === String(component?.productId))
        || candidates.find((product) => product.handle && product.handle === component?.productHandle)
        || candidates.find((product) => String(product.title || '').toLowerCase() === String(component?.productTitle || '').toLowerCase());
      return matched?.replacementFilter || null;
    }

    addedFilterOptions() {
      return this.filterOptions().filter((option) => this.filterSelection(option).added);
    }

    addedFilterCount() {
      return this.addedFilterOptions().length;
    }

    filterVariant(option) {
      const selection = this.filterSelection(option);
      return Core().availableVariant(option?.product, selection.variantId);
    }

    filterPlan(option) {
      const selection = this.filterSelection(option);
      const variant = this.filterVariant(option);
      return variant?.sellingPlans?.find((plan) => String(plan.id) === String(selection.planId)) || variant?.sellingPlans?.[0] || null;
    }

    syncFilterPlan(option) {
      const selection = this.filterSelection(option);
      const variant = this.filterVariant(option);
      if (!variant) return;
      selection.variantId = variant.id;
      const plan = this.filterPlan(option);
      selection.planId = plan?.id || null;
      if (!plan && selection.purchase === 'subscription') selection.purchase = 'once';
    }

    toggleFilter(key) {
      const option = this.filterOptions().find((item) => item.key === String(key));
      if (!option) return;
      this.syncFilterPlan(option);
      const selection = this.filterSelection(option);
      selection.added = !selection.added;
      this.emit(selection.added ? 'setup_product_added' : 'setup_product_removed', { ...this.metrics(), product: option.product.title });
      this.render();
    }

    filterOptionPrice(option) {
      const selection = this.filterSelection(option);
      if (!selection.added) return 0;
      const variant = this.filterVariant(option);
      if (!variant) return 0;
      const plan = selection.purchase === 'subscription' ? this.filterPlan(option) : null;
      return Number(plan?.price ?? variant.price);
    }

    filterPrice() {
      return this.addedFilterOptions().reduce((total, option) => total + this.filterOptionPrice(option), 0);
    }

    setupTotal() {
      if (this.state.mode === 'build-own') return this.ownTotal();
      return this.pricingResult().total;
    }

    back() {
      if (this.state.screen === 'own') { this.state.screen = 'start'; this.state.mode = 'recommend'; this.render(); return; }
      const order = ['start', 'rooms', 'needs', 'setup', 'customise'];
      const index = order.indexOf(this.state.screen);
      if (index <= 0) { this.close(); return; }
      this.state.screen = order[index - 1];
      this.render();
    }

    renderProgress() {
      if (this.state.screen === 'start') {
        this.progress.innerHTML = '';
        this.querySelector('[data-setup-back]').hidden = true;
        return;
      }
      if (this.state.screen === 'own') {
        this.progress.innerHTML = '<span class="setup-builder__mobile-step">Build your own bundle</span><span class="setup-builder__progress-step is-active" aria-current="step"><i>1</i>Choose products</span><span class="setup-builder__progress-step"><i>2</i>Review basket</span>';
        this.querySelector('[data-setup-back]').hidden = false;
        return;
      }
      const step = this.state.screen === 'rooms' || this.state.screen === 'needs' ? 1 : this.state.screen === 'setup' ? 2 : 3;
      this.progress.innerHTML = `<span class="setup-builder__mobile-step">Step ${step} of 3</span>${['Your home', 'Your setup', 'Customise'].map((label, index) => `<span class="setup-builder__progress-step${index + 1 <= step ? ' is-active' : ''}"${index + 1 === step ? ' aria-current="step"' : ''}><i>${index + 1}</i>${escapeHtml(label)}</span>`).join('')}`;
      this.querySelector('[data-setup-back]').hidden = false;
    }

    render() {
      this.view.querySelectorAll('[data-ugc-carousel]').forEach((section) => section.ugcCleanup?.());
      this.renderProgress();
      if (this.state.screen === 'start') this.view.innerHTML = this.renderStart();
      if (this.state.screen === 'own') this.view.innerHTML = this.renderBuildOwn();
      if (this.state.screen === 'rooms') this.view.innerHTML = this.renderRooms();
      if (this.state.screen === 'needs') this.view.innerHTML = this.renderNeeds();
      if (this.state.screen === 'setup') this.view.innerHTML = this.renderSetup(false);
      if (this.state.screen === 'customise') this.view.innerHTML = this.renderSetup(true);
      window.luxenUgcCarousel?.init(this.view);
      const filterSubscription = this.querySelector('.setup-filter-subscription');
      if (filterSubscription) {
        filterSubscription.id = 'SetupFilterSubscription';
        filterSubscription.tabIndex = -1;
      }
    }

    renderIntro(eyebrow, heading, text) {
      return `<div class="setup-builder__intro"><p>${escapeHtml(eyebrow)}</p><h1 tabindex="-1">${escapeHtml(heading)}</h1>${text ? `<div>${escapeHtml(text)}</div>` : ''}</div>`;
    }

    renderStart() {
      const copy = this.config.copy || {};
      const recommendCard = `<button class="setup-builder-choice setup-builder-choice--primary" type="button" data-setup-action="choose-recommend"><span>01</span><strong>${escapeHtml(copy.recommendTitle || 'Recommend my Luxen setup')}</strong><small>${escapeHtml(copy.recommendText || 'Answer a few questions and we’ll match the right Luxen products to your rooms and needs.')}</small><b>${escapeHtml(copy.recommendLabel || 'Start recommendation')} <i aria-hidden="true">→</i></b></button>`;
      return `<div class="setup-builder__single setup-builder__single--choice"><main>${this.renderIntro(copy.choiceEyebrow || 'BUILD YOUR SETUP', copy.choiceHeading || 'Build your Luxen setup your way', copy.choiceText || '')}<div class="setup-builder-choices">${recommendCard}</div></main></div>`;
    }

    renderBuildOwn() {
      const products = this.config.buildOwnProducts || [];
      const cards = products.map((item) => {
        const selection = this.ownSelection(item);
        const roleCount = products.reduce((total, product) => total + (product.role === item.role ? Number(this.state.ownSelections[product.id]?.quantity || 0) : 0), 0);
        const roleCap = item.role === 'filter' ? item.maxQuantity : Math.min(item.maxQuantity, Number(this.config.pricing?.caps?.[item.role] || item.maxQuantity));
        const variants = item.product.variants.filter((variant) => variant.available);
        const image = selection.variant?.image || item.product.featuredImage;
        const unitSelectors = variants.length > 1 && item.role === 'fan' ? selection.variants.map((variant, index) => `<label class="setup-own-product__variant"><span>Fan ${index + 1} colour</span><select data-own-variant="${escapeHtml(item.id)}" data-own-unit="${index}">${variants.map((option) => `<option value="${option.id}"${option.id === variant?.id ? ' selected' : ''}>${escapeHtml(option.title)}</option>`).join('')}</select></label>`).join('') : '';
        const options = variants.length > 1 && item.role !== 'fan' ? `<label class="setup-own-product__variant"><span>Choose option</span><select data-own-variant="${escapeHtml(item.id)}" data-own-unit="0">${variants.map((variant) => `<option value="${variant.id}"${variant.id === selection.variant?.id ? ' selected' : ''}>${escapeHtml(variant.title)} — ${escapeHtml(this.money(variant.price))}</option>`).join('')}</select></label>` : '';
        const fanVariantSelectors = item.role === 'fan' ? unitSelectors : '';
        const lineTotal = selection.variants.reduce((sum, variant) => sum + Number(variant?.price || 0), 0);
        const capHit = item.role !== 'filter' && roleCount >= Number(this.config.pricing?.caps?.[item.role] || item.maxQuantity);
        return `<article class="setup-own-product"><div class="setup-own-product__media">${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(item.product.title)}" loading="lazy" width="260" height="260">` : ''}</div><div class="setup-own-product__body"><div class="setup-own-product__heading"><div><p>${escapeHtml(item.role === 'filter' ? 'SPARE FILTER' : item.role.toUpperCase())}</p><h2>${escapeHtml(item.product.title)}</h2></div><strong>${escapeHtml(this.money(selection.variant?.price || 0))}</strong></div>${item.copy ? `<p class="setup-own-product__copy">${escapeHtml(item.copy)}</p>` : ''}${options}${fanVariantSelectors}<div class="setup-own-product__controls"><div class="setup-quantity" aria-label="${escapeHtml(item.product.title)} quantity"><button type="button" data-setup-action="own-quantity" data-own-product="${escapeHtml(item.id)}" data-delta="-1" aria-label="Remove one ${escapeHtml(item.product.title)}" ${selection.quantity === 0 ? 'disabled' : ''}>−</button><output aria-live="polite">${selection.quantity}</output><button type="button" data-setup-action="own-quantity" data-own-product="${escapeHtml(item.id)}" data-delta="1" aria-label="Add one ${escapeHtml(item.product.title)}" ${selection.quantity >= roleCap || capHit ? 'disabled' : ''}>+</button></div><span>Line total <b>${escapeHtml(this.money(lineTotal))}</b></span></div></div></article>`;
      }).join('');
      const empty = '<p class="setup-builder__empty-own">No products have been added to the build-your-own list yet. Add products with the “Build-your-own product” block in the Theme Editor.</p>';
      const count = this.ownProductCount();
      const total = this.ownTotal();
      const applianceCount = this.ownApplianceCount();
      const minimumMet = applianceCount >= 2;
      const addLabel = minimumMet ? 'Add bundle to basket' : 'Select 2 appliances to continue';
      const addButton = `<button type="button" data-setup-action="add-to-cart" ${minimumMet ? '' : 'disabled'}>${addLabel} <span aria-hidden="true">→</span></button>`;
      const minimumNote = '<small class="setup-own-summary__minimum">Choose at least 2 appliances. Spare filters do not count toward the minimum.</small>';
      return `<div class="setup-builder__workspace setup-builder__workspace--own"><main>${this.renderIntro('BUILD YOUR OWN', 'Choose your products', 'Select the products and quantities for your bundle. Eligible bundle savings are applied automatically in your basket.')}${products.length ? `<div class="setup-builder__products setup-builder__products--own">${cards}</div>` : empty}<div class="setup-builder__status" data-setup-status role="status" aria-live="polite"></div></main><aside class="setup-builder__sidebar"><section class="setup-own-summary"><h2>Your bundle</h2><p>${count} ${count === 1 ? 'product' : 'products'} selected · ${applianceCount} ${applianceCount === 1 ? 'appliance' : 'appliances'}</p><strong>${escapeHtml(this.money(total))}</strong>${minimumMet ? '<small>Eligible bundle savings are applied automatically.</small>' : minimumNote}</section>${addButton}</aside><footer class="setup-builder__mobile-summary"><div><span>${count} ${count === 1 ? 'product' : 'products'}</span><strong>${escapeHtml(this.money(total))}</strong></div>${addButton}<div class="setup-builder__status" data-setup-status role="status" aria-live="polite"></div></footer></div>`;
    }

    renderTrustReviews() {
      const trust = this.config.trust || {};
      const reviews = (trust.reviews || []).slice(0, 3);
      if (!reviews.length && !trust.rating && !trust.count) return '';
      const rating = trust.rating ? `${escapeHtml(trust.rating)}/5` : '';
      const count = trust.count ? `${escapeHtml(trust.count)} verified reviews` : '';
      const cards = reviews.map((review) => `<blockquote class="setup-trust-review"><div aria-label="5 out of 5 stars">★★★★★</div><p>“${escapeHtml(review.quote)}”</p>${review.author || review.context ? `<footer><strong>${escapeHtml(review.author)}</strong>${review.context ? `<span>${escapeHtml(review.context)}</span>` : ''}</footer>` : ''}</blockquote>`).join('');
      return `<aside class="setup-trust" aria-label="Customer reviews"><header><div><p>VERIFIED CUSTOMER REVIEWS</p><h2>${escapeHtml(trust.reviewsHeading || 'Trusted by Luxen customers')}</h2></div>${rating || count ? `<strong><span aria-hidden="true">★</span> ${rating}${rating && count ? ' · ' : ''}${count}</strong>` : ''}</header>${cards ? `<div class="setup-trust__reviews">${cards}</div>` : ''}</aside>`;
    }

    renderTrustStories() {
      if (this.trustStoriesMarkup) return `<div class="setup-stories setup-stories--carousel">${this.trustStoriesMarkup}</div>`;
      return this.trustStoriesLoading ? '<section class="setup-stories setup-stories--loading" aria-live="polite">Loading customer stories…</section>' : '';
    }

    async loadTrustStories() {
      if (this.trustStoriesLoaded || this.trustStoriesLoading) return;
      this.trustStoriesLoading = true;
      if (['setup', 'customise'].includes(this.state.screen)) this.render();
      try {
        const response = await fetch(this.config.trust?.ugcSourceUrl || '/', { headers: { accept: 'text/html' } });
        if (!response.ok) throw new Error('Customer stories unavailable');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const carousel = page.querySelector('[data-ugc-carousel]');
        const source = carousel?.closest('.shopify-section');
        if (!source || !carousel.querySelector('[data-ugc-stories]')) return;
        source.querySelectorAll('script:not([type="application/json"])').forEach((script) => script.remove());
        // Give the cloned section its own IDs when the builder opens on the homepage.
        source.querySelectorAll('[id]').forEach((element) => {
          const previous = element.id;
          element.id = `builder-${previous}`;
          source.querySelectorAll('[aria-labelledby], [aria-controls]').forEach((target) => {
            for (const attribute of ['aria-labelledby', 'aria-controls']) {
              const value = target.getAttribute(attribute);
              if (value) target.setAttribute(attribute, value.split(' ').map((id) => id === previous ? element.id : id).join(' '));
            }
          });
        });
        this.trustStoriesMarkup = source.outerHTML;
      } catch (error) {
        this.trustStoriesMarkup = '';
      } finally {
        this.trustStoriesLoading = false;
        this.trustStoriesLoaded = true;
        if (this.dialog.open && ['setup', 'customise'].includes(this.state.screen)) this.render();
      }
    }

    scrollTrustStories(direction) {
      const viewport = this.querySelector('[data-setup-stories]');
      if (!viewport) return;
      viewport.scrollBy({ left: direction * Math.max(280, viewport.clientWidth * .72), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    viewFilterOptions() {
      if (!this.eligibleForFilter()) return;
      if (this.state.screen !== 'customise') {
        this.state.screen = 'customise';
        this.render();
        this.loadTrustStories();
      }
      requestAnimationFrame(() => {
        this.querySelector('#SetupFilterSubscription')?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start'
        });
      });
    }

    renderRooms() {
      const cards = this.config.rooms.map((room) => {
        const selected = this.state.selectedRooms.includes(room.key);
        return `<button class="setup-room${selected ? ' is-selected' : ''}" type="button" data-setup-action="toggle-room" data-room-key="${escapeHtml(room.key)}" aria-pressed="${selected}">${room.image ? `<span class="setup-room__media"><img src="${escapeHtml(room.image)}" alt="" loading="lazy" width="450" height="320">${selected ? `<i aria-hidden="true"><img src="${escapeHtml(this.config.icons.check)}" alt="" width="10" height="10"></i>` : ''}</span>` : '<span class="setup-room__media setup-room__media--empty" aria-hidden="true"></span>'}<span class="setup-room__label"><strong>${escapeHtml(room.title)}</strong>${selected ? '<small>Selected</small>' : ''}</span></button>`;
      }).join('');
      const count = this.state.selectedRooms.length;
      const presetNeed = this.need(this.state.presetNeedKey);
      const preset = presetNeed ? `<p class="setup-builder__preset"><span aria-hidden="true">✓</span> ${escapeHtml(presetNeed.label)} preselected${count ? '' : ' — choose a room to apply it'}</p>` : '';
      return `<div class="setup-builder__single"><main>${this.renderIntro(this.config.copy.roomsEyebrow, this.config.copy.roomsHeading, this.config.copy.roomsText)}${preset}<div class="setup-builder__rooms">${cards || '<p>No rooms have been configured yet.</p>'}</div>${this.renderTrustReviews()}</main><footer class="setup-builder__sticky"><p><i></i>${count} ${count === 1 ? 'room' : 'rooms'} selected</p><button type="button" data-setup-action="rooms-next" ${count ? '' : 'disabled'}>Continue <span aria-hidden="true">→</span></button></footer></div>`;
    }

    availableNeeds(room) {
      if (!room.needKeys.length) return this.config.needs;
      return this.config.needs.filter((need) => room.needKeys.includes(need.key));
    }

    renderNeeds() {
      const cards = this.state.selectedRooms.map((roomKey) => {
        const room = this.room(roomKey);
        const selected = this.state.needsByRoom[roomKey] || [];
        const needs = this.availableNeeds(room).map((need) => `<button type="button" class="setup-need${selected.includes(need.key) ? ' is-selected' : ''}" data-setup-action="toggle-need" data-room-key="${escapeHtml(roomKey)}" data-need-key="${escapeHtml(need.key)}" aria-pressed="${selected.includes(need.key)}">${escapeHtml(need.label)}</button>`).join('');
        return `<section class="setup-needs-card"><header>${room.image ? `<img src="${escapeHtml(room.image)}" alt="" loading="lazy" width="88" height="66">` : ''}<div><p>YOUR ${escapeHtml(room.title).toUpperCase()}</p><h2>What would you like to improve?</h2></div></header><div class="setup-needs-card__options">${needs || '<p>No needs are configured for this room.</p>'}</div></section>`;
      }).join('');
      const customised = this.state.selectedRooms.filter((key) => (this.state.needsByRoom[key] || []).length).length;
      return `<div class="setup-builder__single"><main>${this.renderIntro('STEP 1 OF 3', this.config.copy.needsHeading, this.config.copy.needsText)}<div class="setup-builder__selected-tabs">${this.state.selectedRooms.map((key) => `<span>${escapeHtml(this.room(key)?.title)}</span>`).join('')}</div><div class="setup-builder__needs">${cards}</div></main><footer class="setup-builder__sticky"><p><i></i>${customised} ${customised === 1 ? 'room' : 'rooms'} customised</p><button type="button" data-setup-action="needs-next" ${customised === this.state.selectedRooms.length ? '' : 'disabled'}>Build my setup <span aria-hidden="true">→</span></button></footer></div>`;
    }

    recommendationGroups() {
      const groups = [];
      const byProduct = new Map();
      this.state.selectedRooms.forEach((roomKey) => {
        const selection = this.state.selections[roomKey];
        const resolved = Core().resolveSelection(this.config, selection);
        if (!resolved || selection.removed) {
          groups.push({ roomKeys: [roomKey] });
          return;
        }
        const key = String(resolved.product.id);
        if (byProduct.has(key)) {
          byProduct.get(key).roomKeys.push(roomKey);
        } else {
          const group = { roomKeys: [roomKey] };
          byProduct.set(key, group);
          groups.push(group);
        }
      });
      return groups;
    }

    recommendationCard(roomKey, customise, groupedRoomKeys = [roomKey]) {
      const room = this.room(roomKey);
      const selection = this.state.selections[roomKey];
      const resolved = Core().resolveSelection(this.config, selection);
      const selectedNeeds = [...new Set(groupedRoomKeys.flatMap((key) => (this.state.needsByRoom[key] || []).map((needKey) => this.need(needKey)?.label).filter(Boolean)))];
      if (!resolved) return `<article class="setup-product setup-product--empty"><div><span class="setup-product__room">${escapeHtml(room?.title)}</span><h2>${escapeHtml(this.config.copy.emptyRecommendation)}</h2><button type="button" class="setup-link" data-setup-action="add-room">Edit room choices</button></div></article>`;
      const { rule, product, variant } = resolved;
      if (selection.removed) return `<article class="setup-product setup-product--removed"><div><span class="setup-product__room">${escapeHtml(room.title)}</span><h2>${escapeHtml(product.title)} removed</h2><button type="button" class="setup-link" data-setup-action="restore-product" data-room-key="${escapeHtml(roomKey)}">Add it back</button></div></article>`;
      const groupedUnits = groupedRoomKeys.flatMap((key) => {
        const groupedSelection = this.state.selections[key];
        const groupedResolved = Core().resolveSelection(this.config, groupedSelection);
        return this.selectionVariants(groupedSelection, groupedResolved).map((unitVariant, unitIndex) => ({ roomKey: key, unitIndex, variant: unitVariant }));
      });
      const totalQuantity = groupedUnits.length;
      const cardTotal = groupedUnits.reduce((sum, unit) => sum + Number(unit.variant.price || 0), 0);
      const roomKeysValue = groupedRoomKeys.join(',');
      const roomBadges = groupedRoomKeys.map((key) => `<span class="setup-product__room">${escapeHtml(this.room(key)?.title)}</span>`).join('');
      const variants = product.variants.filter((item) => item.available);
      const optionMarkup = (selectedVariant, attributes) => `<select data-setup-variant ${attributes}>${variants.map((item) => `<option value="${item.id}"${item.id === selectedVariant.id ? ' selected' : ''}>${escapeHtml(item.title)} — ${escapeHtml(this.money(item.price))}</option>`).join('')}</select>`;
      let variantControl = '';
      if (variants.length > 1 && customise) {
        variantControl = `<div class="setup-product__unit-options">${groupedUnits.map((unit, index) => `<label class="setup-product__variant"><span>${escapeHtml(totalQuantity > 1 ? `Option ${index + 1}` : 'Choose option')}</span>${optionMarkup(unit.variant, `data-room-key="${escapeHtml(unit.roomKey)}" data-unit-index="${unit.unitIndex}"`)}</label>`).join('')}</div>`;
      } else if (variants.length > 1) {
        variantControl = `<label class="setup-product__variant"><span>Choose option</span>${optionMarkup(variant, `data-room-keys="${escapeHtml(roomKeysValue)}" data-unit-index="all"`)}</label>`;
      }
      const changeAction = groupedRoomKeys.length > 1 ? `data-setup-action="switch-product-group" data-room-keys="${escapeHtml(roomKeysValue)}"` : `data-setup-action="switch-product" data-room-key="${escapeHtml(roomKey)}"`;
      const actions = `<div class="setup-product__actions">${rule.alternative ? `<button type="button" ${changeAction}>Change product</button>` : ''}${customise ? `<button type="button" data-setup-action="remove-product" data-room-key="${escapeHtml(roomKey)}">Remove</button>` : ''}</div>`;
      const role = this.productRole(resolved);
      const roleAtCap = Number(this.applianceCounts()[role] || 0) >= Number(this.config.pricing?.caps?.[role] || 10);
      const quantity = customise ? `<div class="setup-product__quantity-wrap"><span>${escapeHtml(rule.quantityCopy || 'Cover another room?')}</span><div class="setup-quantity" aria-label="${escapeHtml(product.title)} quantity"><button type="button" data-setup-action="quantity" data-room-key="${escapeHtml(roomKey)}" data-delta="-1" aria-label="Decrease ${escapeHtml(product.title)} quantity" ${selection.quantity <= 1 ? 'disabled' : ''}>−</button><output aria-live="polite">${selection.quantity}</output><button type="button" data-setup-action="quantity" data-room-key="${escapeHtml(roomKey)}" data-delta="1" aria-label="Increase ${escapeHtml(product.title)} quantity" ${roleAtCap ? 'disabled' : ''}>+</button></div>${roleAtCap && role !== 'other' ? `<small class="setup-product__cap">Maximum ${escapeHtml(role)} quantity reached</small>` : ''}</div>` : '';
      const recommendationQuantity = !customise && totalQuantity > 1 ? `<span class="setup-product__recommended-quantity" aria-label="Quantity ${totalQuantity}, recommended for ${groupedRoomKeys.length} rooms"><strong>×${totalQuantity}</strong><small>for ${groupedRoomKeys.length} rooms</small></span>` : '';
      return `<article class="setup-product"><div class="setup-product__media">${(variant.image || product.featuredImage) ? `<img src="${escapeHtml(variant.image || product.featuredImage)}" alt="${escapeHtml(product.title)}" loading="lazy" width="320" height="320">` : '<span aria-hidden="true"></span>'}</div><div class="setup-product__content"><div class="setup-product__top"><div class="setup-product__rooms">${roomBadges}</div>${quantity}${recommendationQuantity}</div><div class="setup-product__title"><h2>${escapeHtml(product.title)}</h2><strong>${escapeHtml(this.money(cardTotal))}</strong></div>${selectedNeeds.length ? `<div class="setup-product__tags">${selectedNeeds.map((label) => `<span>${escapeHtml(label)}</span>`).join('')}</div>` : ''}${variantControl}<div class="setup-product__why"><strong>WHY THIS FITS</strong><p>${escapeHtml(rule.copy || `Selected for the needs you chose across these rooms.`)}</p></div>${actions}</div></article>`;
    }

    bundleRecommendationCard(customise) {
      const offer = this.recommendedBundle();
      const variant = this.recommendedBundleVariant();
      if (!offer || !variant) return '';
      const compareAt = this.bundleSeparateTotal(variant);
      const saving = compareAt > variant.price ? compareAt - Number(variant.price || 0) : 0;
      const componentKey = String(variant.id);
      const expectsLiveComponents = Boolean(this.config.storefrontApi?.token && this.config.storefrontApi?.domain);
      const canUseManualContents = this.canUseManualBundleContents(variant);
      const contentItems = this.bundleComponentItems(offer, { fallback: canUseManualContents });
      const selectors = customise ? '' : (offer.product.options || []).map((option, index) => {
        if (!option.values?.length || (option.values.length === 1 && option.values[0] === 'Default Title')) return '';
        const legend = this.bundleOptionLabel(option, index);
        return `<fieldset class="setup-bundle-options"><legend>${escapeHtml(legend)}</legend><div class="setup-bundle-options__values">${option.values.map((value) => {
          const selected = String(variant.options?.[index]) === String(value);
          const available = this.bundleOptionValueAvailable(offer, variant, index, value);
          return `<label class="setup-bundle-option${selected ? ' is-selected' : ''}${available ? '' : ' is-unavailable'}"><input type="radio" name="setup-bundle-${escapeHtml(offer.id)}-${index}" value="${escapeHtml(value)}" data-setup-bundle-option="${index}"${selected ? ' checked' : ''}${available ? '' : ' disabled'}><span>${escapeHtml(value)}</span></label>`;
        }).join('')}</div></fieldset>`;
      }).join('');
      const unavailable = !variant.available ? '<p class="setup-summary__notice">This colour combination is currently unavailable. Join the waitlist or choose an available option.</p>' : '';
      const missingCompareAt = this.config.designMode && !saving ? '<p class="setup-builder__price-notice"><strong>Theme editor:</strong> Add a compare-at price to this Shopify Bundle variant to display its saving.</p>' : '';
      let componentStatus = '';
      if (expectsLiveComponents) {
        if (this.bundleComponentRequests.has(componentKey) || !this.bundleComponentCache.has(componentKey) && !this.bundleComponentErrors.has(componentKey)) {
          componentStatus = '<p class="setup-builder__price-notice">Loading bundle contents…</p>';
        } else if (this.config.designMode && this.bundleComponentErrors.has(componentKey)) {
          componentStatus = '<p class="setup-builder__price-notice"><strong>Theme editor:</strong> Shopify bundle contents could not be loaded. The configured product fallback is shown.</p>';
        } else if (this.config.designMode && this.bundleComponentCache.has(componentKey) && !this.bundleComponentCache.get(componentKey).length) {
          componentStatus = '<p class="setup-builder__price-notice"><strong>Theme editor:</strong> Shopify returned no components for this variant. The configured product fallback is shown.</p>';
        }
      }
      const includesFilter = contentItems.some((item) => /filter/i.test(item.title)) || Number(offer.filter || 0) > 0;
      const subscriptionCta = this.eligibleForFilter() ? '<a class="setup-builder__filter-jump setup-builder__filter-jump--breakdown" href="#SetupFilterSubscription" data-setup-action="view-filter-options"><span class="setup-builder__filter-jump-copy"><strong>Set up filter replacements</strong><small>Fresh filters delivered automatically when you need them.</small></span><span class="setup-builder__filter-jump-arrow" aria-hidden="true">↓</span></a>' : '';
      const breakdown = contentItems.length ? `<ul class="setup-bundle-included" aria-label="Bundle contents">${contentItems.map((item) => this.bundleIncludedRow(item)).join('')}</ul>${subscriptionCta}` : '';
      const reasons = this.recommendationReasons(offer);
      const rationale = `<section class="setup-bundle-rationale"><h3>Why we recommended this</h3>${reasons.length ? `<ul>${reasons.map((reason) => `<li><span aria-hidden="true">✓</span>${escapeHtml(reason)}</li>`).join('')}</ul>` : `<p>${escapeHtml(offer.copy || 'Selected around the rooms and needs you chose.')}</p>`}</section>`;
      const priceRow = `<div class="setup-bundle-price" aria-label="Bundle price${saving ? `, saving ${this.money(saving)}` : ''}"><strong>${escapeHtml(this.money(variant.price))}</strong>${compareAt > variant.price ? `<s><span class="visually-hidden">Products separately </span>${escapeHtml(this.money(compareAt))}</s><span>Save ${escapeHtml(this.money(saving))}</span>` : ''}</div>`;
      const heroImage = this.bundleHeroImage(offer, variant);
      const bundleTitle = this.bundleDisplayTitle(offer);
      const bundleMeta = offer.product.title && offer.product.title !== bundleTitle ? offer.product.title : 'Fixed bundle offer';
      return `<article class="setup-product setup-product--bundle"><header class="setup-bundle-header"><span class="setup-bundle-badge">Recommended for your home</span><h2>${escapeHtml(bundleTitle)}</h2><p>${escapeHtml(bundleMeta)}</p>${priceRow}</header><div class="setup-product__media">${heroImage ? `<img src="${escapeHtml(heroImage)}" alt="${escapeHtml(offer.product.title)}" loading="lazy" width="320" height="320">` : '<span aria-hidden="true"></span>'}</div><div class="setup-product__content">${breakdown}${missingCompareAt}${componentStatus}${selectors ? `<div class="setup-bundle-config">${selectors}</div>` : ''}${rationale}${unavailable}</div></article>`;
    }

    bundleOptionLabel(option, index) {
      let name = String(option?.name || '').replace(/\s*\((?:colou?r)\)\s*$/i, '').trim();
      (option?.values || []).forEach((value) => {
        const escaped = String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        name = name.replace(new RegExp(`\\s*\\(${escaped}\\)\\s*$`, 'i'), '').trim();
      });
      if (/colou?r/i.test(String(option?.name || ''))) return (this.recommendedBundle()?.product?.options || []).length === 1 ? 'Choose your Luxen Air colour' : `Choose ${name || `item ${index + 1}`} colour`;
      return `Choose ${name || `option ${index + 1}`}`;
    }

    canUseManualBundleContents(variant) {
      if (!variant) return true;
      const componentKey = String(variant.id);
      const expectsLiveComponents = Boolean(this.config.storefrontApi?.token && this.config.storefrontApi?.domain);
      const componentCache = this.bundleComponentCache.get(componentKey);
      return !expectsLiveComponents || this.bundleComponentErrors.has(componentKey) || (this.bundleComponentCache.has(componentKey) && !componentCache?.length);
    }

    bundleOptionValueAvailable(offer, current, optionIndex, value) {
      const values = [...(current.options || [])];
      values[optionIndex] = value;
      return offer.product.variants.some((candidate) => candidate.options?.every((candidateValue, index) => String(candidateValue) === String(values[index])));
    }

    bundleHeroImage(offer, variant) {
      if (variant?.hasVariantImage && variant.image) return variant.image;
      const components = variant ? this.bundleComponentCache?.get(String(variant.id)) : null;
      const selectedValues = (variant?.options || []).map((value) => String(value).toLowerCase());
      const matchingComponent = components?.find((component) => component.image && selectedValues.some((value) => String(component.variantTitle || '').toLowerCase().includes(value)));
      return matchingComponent?.image || components?.find((component) => component.image)?.image || variant?.image || offer?.product?.featuredImage || '';
    }

    cleanVariantTitle(productTitle, variantTitle) {
      const title = String(variantTitle || '').trim();
      if (!title || title === 'Default Title') return '';
      if (String(productTitle || '').toLowerCase().includes(title.toLowerCase())) return '';
      return title.replace(/^default\s*\/\s*/i, '').trim();
    }

    bundleProductRow(item, showImage = false) {
      const media = showImage ? `<span class="setup-bundle-product__media">${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.imageAlt || '')}" loading="lazy" width="64" height="64">` : '<i aria-hidden="true"></i>'}</span>` : '';
      const meta = [item.variant, `Qty ${item.quantity}`].filter(Boolean).join(' · ');
      return `<li class="setup-bundle-product">${media}<span class="setup-bundle-product__details"><strong>${escapeHtml(item.title)}</strong><small>${escapeHtml(meta)}</small></span>${item.lineTotal ? `<b>${escapeHtml(this.money(item.lineTotal))}</b>` : ''}</li>`;
    }

    bundleIncludedRow(item) {
      const variant = item.variant ? `<small>${escapeHtml(item.variant)}</small>` : '';
      return `<li class="setup-bundle-included__item"><strong>${escapeHtml(item.quantity)}x</strong><span>${escapeHtml(item.title)}${variant}</span></li>`;
    }

    bundleDisplayTitle(offer) {
      return String(offer?.label || offer?.product?.title || 'Fixed bundle offer').trim();
    }

    bundleConfiguredItem(line) {
      const value = String(line || '').trim();
      const match = value.match(/^(\d+)\s*[x×]\s*(.+)$/i);
      return {
        title: (match ? match[2] : value).trim(),
        variant: '',
        quantity: match ? Math.max(1, Number(match[1] || 1)) : 1,
        lineTotal: 0,
        image: ''
      };
    }

    bundleRoleProduct(offer, role) {
      const product = offer?.items?.[role];
      if (product?.variants?.length) return product;
      if (role === 'filter') return this.config.filterProduct || null;
      return this.config.buildOwnProducts?.find((item) => item.role === role)?.product || null;
    }

    bundleConfiguredVariant(product, role) {
      const variant = this.recommendedBundleVariant();
      const colourOption = (product.options || []).findIndex((option) => /colou?r/i.test(String(option.name || '')));
      if (colourOption < 0) return Core().availableVariant(product) || product.variants?.[0] || null;
      const rolePattern = role === 'fan' ? /fan|luxen air|3\s*in\s*1/i : role === 'purifier' ? /purifier/i : role === 'vacuum' ? /vacuum/i : /filter/i;
      const selected = (this.recommendedBundle()?.product?.options || []).map((option, index) => ({
        name: String(option.name || ''),
        value: variant?.options?.[index]
      })).find((option) => /colou?r/i.test(option.name) && rolePattern.test(option.name))?.value
        || (role === 'fan' ? variant?.options?.[(this.recommendedBundle()?.product?.options || []).findIndex((option) => /colou?r/i.test(String(option.name || '')))] : null);
      if (!selected) return Core().availableVariant(product) || product.variants?.[0] || null;
      return product.variants.find((item) => String(item.options?.[colourOption] || '').toLowerCase() === String(selected).toLowerCase())
        || Core().availableVariant(product)
        || product.variants?.[0]
        || null;
    }

    bundleConfiguredProductItems(offer) {
      return [['fan', 'Fan'], ['purifier', 'Air Purifier'], ['vacuum', 'Vacuum'], ['filter', 'Spare Filter']]
        .map(([key, fallbackTitle]) => {
          const quantity = Number(offer?.[key] || 0);
          const product = this.bundleRoleProduct(offer, key);
          if (!quantity || !product) return null;
          const variant = this.bundleConfiguredVariant(product, key);
          return {
            title: product.title || fallbackTitle,
            variant: this.cleanVariantTitle(product.title, variant?.title),
            quantity,
            lineTotal: variant ? quantity * Number(variant.price || 0) : 0,
            image: variant?.image || product.featuredImage || '',
            imageAlt: product.title || fallbackTitle
          };
        })
        .filter(Boolean);
    }

    bundleComponentItems(offer, options = {}) {
      const variant = this.recommendedBundleVariant();
      const components = variant ? this.bundleComponentCache?.get(String(variant.id)) : null;
      if (components?.length) return components.map((component) => ({
        title: component.productTitle,
        variant: this.cleanVariantTitle(component.productTitle, component.variantTitle),
        quantity: component.quantity,
        lineTotal: component.lineTotal,
        image: component.image,
        imageAlt: component.imageAlt || component.productTitle
      }));
      const fallback = options.fallback !== false;
      if (!fallback) return [];
      const configuredProducts = this.bundleConfiguredProductItems(offer);
      if (configuredProducts.length) return configuredProducts;
      const configured = String(offer?.contents || '').split(/\r?\n|\s+\+\s+|\s*\|\s*/).map((item) => item.trim()).filter(Boolean);
      if (configured.length) return configured.map((item) => this.bundleConfiguredItem(item));
      return [['fan', 'Fan'], ['purifier', 'Air Purifier'], ['vacuum', 'Vacuum'], ['filter', 'Spare Filter']]
        .filter(([key]) => Number(offer?.[key] || 0) > 0)
        .map(([key, title]) => ({ title, variant: '', quantity: Number(offer[key]), lineTotal: 0, image: '' }));
    }

    bundleContentItems(offer) {
      return this.bundleComponentItems(offer).map((item) => ({ label: `${item.quantity}× ${item.title}${item.variant ? ` — ${item.variant}` : ''}`, lineTotal: item.lineTotal }));
    }

    recommendationReasons(offer) {
      const reasons = this.state.selectedRooms.map((roomKey) => {
        const room = this.room(roomKey);
        const needs = (this.state.needsByRoom[roomKey] || []).map((key) => this.need(key)?.label).filter(Boolean);
        if (!room) return '';
        return needs.length ? `${needs.join(', ')} for your ${room.title}` : `Selected for your ${room.title}`;
      }).filter(Boolean);
      const includesFilter = this.bundleComponentItems(offer).some((item) => /filter/i.test(item.title)) || Number(offer?.filter || 0) > 0;
      if (includesFilter) reasons.push('Replacement filter included');
      return [...new Set(reasons)].slice(0, 4);
    }

    bundleContents(offer) {
      return this.bundleContentItems(offer).map((item) => item.label);
    }

    bundleComponentStorageKey(cacheKey) {
      return `luxen:setup-builder:bundle-components:${cacheKey}`;
    }

    readStoredBundleComponents(cacheKey) {
      try {
        const payload = JSON.parse(window.localStorage?.getItem(this.bundleComponentStorageKey(cacheKey)) || 'null');
        if (!payload || Date.now() - Number(payload.savedAt || 0) > 86400000 || !Array.isArray(payload.components)) return null;
        return payload.components;
      } catch (error) {
        return null;
      }
    }

    storeBundleComponents(cacheKey, components) {
      if (!components?.length) return;
      try {
        window.localStorage?.setItem(this.bundleComponentStorageKey(cacheKey), JSON.stringify({ savedAt: Date.now(), components }));
      } catch (error) {
        // Storage is an optimisation only.
      }
    }

    async loadBundleComponents() {
      const api = this.config.storefrontApi;
      const variant = this.recommendedBundleVariant();
      if (!this.state.useRecommendedBundle || !variant || !api?.token || !api?.domain) return;
      const cacheKey = String(variant.id);
      if (this.bundleComponentCache.has(cacheKey) || this.bundleComponentRequests.has(cacheKey)) return;
      const stored = this.readStoredBundleComponents(cacheKey);
      if (stored?.length) {
        this.bundleComponentCache.set(cacheKey, stored);
        this.bundleComponentErrors.delete(cacheKey);
        this.render();
        return;
      }

      const query = `query LuxenBundleComponents($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            components(first: 30) {
              nodes {
                quantity
                productVariant {
                  id
                  title
                  price { amount currencyCode }
                  compareAtPrice { amount currencyCode }
                  image { url altText }
                  product {
                    id
                    handle
                    title
                    featuredImage { url altText }
                  }
                }
              }
            }
          }
        }
      }`;
      const request = fetch(`https://${api.domain}/api/${api.version || '2026-07'}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': api.token
        },
        body: JSON.stringify({ query, variables: { id: `gid://shopify/ProductVariant/${variant.id}` } })
      })
        .then(async (response) => {
          const payload = await response.json();
          if (!response.ok || payload.errors?.length) {
            throw new Error(payload.errors?.map((error) => error.message).join('; ') || 'Shopify could not return this bundle\'s components.');
          }
          const nodes = payload.data?.node?.components?.nodes || [];
          if (!nodes.length) {
            console.warn('Luxen setup builder: Shopify returned no bundle components for this variant.', {
              variantId: variant.id,
              bundle: this.recommendedBundle()?.product?.title || this.recommendedBundle()?.label || 'Unknown bundle'
            });
          }
          const components = nodes.map((node) => {
            const quantity = Math.max(1, Number(node.quantity || 1));
            const price = Math.round(Number(node.productVariant?.price?.amount || 0) * 100);
            const compareAtPrice = Math.round(Number(node.productVariant?.compareAtPrice?.amount || 0) * 100);
            return {
              quantity,
              productId: String(node.productVariant?.product?.id || '').split('/').pop(),
              productHandle: node.productVariant?.product?.handle || '',
              productTitle: node.productVariant?.product?.title || 'Bundle item',
              variantTitle: node.productVariant?.title || '',
              price,
              compareAtPrice,
              image: node.productVariant?.image?.url || node.productVariant?.product?.featuredImage?.url || '',
              imageAlt: node.productVariant?.image?.altText || node.productVariant?.product?.featuredImage?.altText || node.productVariant?.product?.title || '',
              lineTotal: quantity * Math.max(price, compareAtPrice)
            };
          });
          this.bundleComponentCache.set(cacheKey, components);
          this.storeBundleComponents(cacheKey, components);
          this.bundleComponentErrors.delete(cacheKey);
        })
        .catch((error) => {
          this.bundleComponentErrors.add(cacheKey);
          console.warn('Luxen setup builder could not load Shopify bundle components.', error);
        })
        .finally(() => {
          this.bundleComponentRequests.delete(cacheKey);
          if (String(this.recommendedBundleVariant()?.id || '') === cacheKey) this.render();
        });

      this.bundleComponentRequests.set(cacheKey, request);
      return request;
    }

    bundleEditorDiagnostics() {
      if (!this.config.designMode || this.state.useRecommendedBundle) return '';
      const rooms = new Set(this.state.selectedRooms);
      const needs = new Set(this.state.selectedRooms.flatMap((roomKey) => this.state.needsByRoom[roomKey] || []));
      if (!this.config.fixedBundles.length) return '<aside class="setup-builder__diagnostic"><strong>Theme editor:</strong> No fixed bundle offers were loaded. Check that the offer is enabled and has a parent product.</aside>';
      const reasons = this.config.fixedBundles.map((offer) => {
        const failures = [];
        if (offer.featured === false) failures.push('Recommend this offer is off');
        if (offer.roomKeys.length && !offer.roomKeys.some((key) => rooms.has(key))) failures.push('room keys do not match');
        if (offer.needKeys.length && !offer.needKeys.some((key) => needs.has(key))) failures.push('need keys do not match');
        if (!offer.product.variants.length) failures.push('Shopify returned no variants for this bundle product');
        if (!(offer.fan + offer.purifier + offer.vacuum + offer.filter)) failures.push('component quantities are needed for exact-match upgrades');
        return `<li><strong>${escapeHtml(offer.label || offer.product.title)}:</strong> ${escapeHtml(failures.join('; ') || 'eligible')}</li>`;
      }).join('');
      return `<aside class="setup-builder__diagnostic"><strong>Theme editor bundle diagnostics</strong><ul>${reasons}</ul></aside>`;
    }

    summary() {
      if (this.state.useRecommendedBundle) {
        const offer = this.recommendedBundle();
        const variant = this.recommendedBundleVariant();
        if (offer && variant) {
          const pricing = this.pricingResult();
          const contentItems = this.bundleComponentItems(offer, { fallback: this.canUseManualBundleContents(variant) });
          const breakdown = contentItems.length ? `<ul class="setup-summary__products">${contentItems.map((item) => this.bundleProductRow(item)).join('')}</ul>` : '';
          const extraFilters = this.addedFilterOptions().map((option) => {
            const selection = this.filterSelection(option);
            return this.bundleProductRow({ title: option.product.title, variant: selection.purchase === 'subscription' ? this.filterPlan(option)?.name || 'Subscription' : 'One-time purchase', quantity: 1, lineTotal: this.filterOptionPrice(option) });
          }).join('');
          const productCount = this.bundleProductCount(offer, variant) + this.addedFilterCount();
          const status = pricing.saving ? '<p class="setup-summary__offer"><span aria-hidden="true">✓</span> Bundle saving applied</p>' : '';
          const bundleTitle = this.bundleDisplayTitle(offer);
          return `<div class="setup-summary setup-summary--order"><p class="setup-summary__eyebrow">Fixed bundle offer</p><h2>${escapeHtml(bundleTitle)}</h2><p class="setup-summary__bundle-count">${productCount} ${productCount === 1 ? 'product' : 'products'}</p>${breakdown}${extraFilters ? `<ul class="setup-summary__products">${extraFilters}</ul>` : ''}${status}${!variant.available ? '<p class="setup-summary__notice">Join the waitlist or choose an available colour combination.</p>' : ''}<dl class="setup-summary__totals"><div><dt>Subtotal</dt><dd>${escapeHtml(this.money(pricing.regularTotal))}</dd></div>${pricing.saving ? `<div class="setup-summary__discount"><dt>Bundle saving</dt><dd>−${escapeHtml(this.money(pricing.saving))}</dd></div>` : ''}<div class="setup-summary__grand-total"><dt>Total</dt><dd>${escapeHtml(this.money(pricing.total))}</dd></div></dl></div>`;
        }
      }
      const items = this.recommendationGroups().map((group) => {
        const roomKey = group.roomKeys[0];
        const selection = this.state.selections[roomKey];
        const resolved = Core().resolveSelection(this.config, selection);
        if (!resolved || selection.removed) return '';
        const variants = group.roomKeys.flatMap((key) => {
          const groupedSelection = this.state.selections[key];
          return this.selectionVariants(groupedSelection, Core().resolveSelection(this.config, groupedSelection));
        });
        const quantity = variants.length;
        const lineTotal = variants.reduce((sum, item) => sum + Number(item.price || 0), 0);
        const optionSummary = [...new Set(variants.map((item) => item.title).filter((title) => title && title !== 'Default Title'))].join(', ');
        const roomNames = group.roomKeys.map((key) => this.room(key)?.title).filter(Boolean).join(' + ');
        return `<li><div><span>${escapeHtml(roomNames)}</span><strong>${escapeHtml(resolved.product.title)}</strong><small>Quantity ${quantity}${optionSummary ? ` · ${escapeHtml(optionSummary)}` : ''}</small></div><b>${escapeHtml(this.money(lineTotal))}</b></li>`;
      }).join('');
      const filterItems = this.addedFilterOptions().map((option) => {
        const selection = this.filterSelection(option);
        return `<li><div><span>Replacement filter</span><strong>${escapeHtml(option.product.title)}</strong><small>${selection.purchase === 'subscription' ? escapeHtml(this.filterPlan(option)?.name || 'Subscription') : 'One-time purchase'}</small></div><b>${escapeHtml(this.money(this.filterOptionPrice(option)))}</b></li>`;
      }).join('');
      const pricing = this.pricingResult();
      const availability = pricing.match?.unavailable ? '<p class="setup-summary__notice">That bundle combination is currently unavailable. Join the waitlist to hear when it becomes available.</p>' : '';
      const offer = pricing.match && !pricing.match.unavailable ? '<p class="setup-summary__offer"><span aria-hidden="true">✓</span> Bundle saving applied</p>' : '';
      return `<div class="setup-summary setup-summary--order"><p class="setup-summary__eyebrow">Your Luxen setup</p><h2>${this.setupUnits().length + this.addedFilterCount()} products</h2><ul class="setup-summary__legacy-products">${(items || filterItems) ? `${items}${filterItems}` : '<li>Your setup is currently empty.</li>'}</ul>${offer}${availability}<dl class="setup-summary__totals"><div><dt>Subtotal</dt><dd>${escapeHtml(this.money(pricing.regularTotal))}</dd></div>${pricing.saving ? `<div class="setup-summary__discount"><dt>Bundle saving</dt><dd>−${escapeHtml(this.money(pricing.saving))}</dd></div>` : ''}<div class="setup-summary__grand-total"><dt>Total</dt><dd>${escapeHtml(this.money(pricing.total))}</dd></div></dl></div>`;
    }

    renderFilterOwnership() {
      if (!this.eligibleForFilter()) return '';
      const options = this.filterOptions();
      if (!options.length) {
        return this.config.copy.filterUrl ? `<aside class="setup-filter"><div><p>${escapeHtml(this.config.copy.filterEyebrow)}</p><h2>${escapeHtml(this.config.copy.filterHeading)}</h2><span>${escapeHtml(this.config.copy.filterText)}</span></div><a href="${escapeHtml(this.config.copy.filterUrl)}">${escapeHtml(this.config.copy.filterLabel)} <span aria-hidden="true">→</span></a></aside>` : '';
      }
      const cards = options.map((option) => {
        this.syncFilterPlan(option);
        const product = option.product;
        const selection = this.filterSelection(option);
        const variant = this.filterVariant(option);
        if (!variant) return '';
        const plans = variant.sellingPlans || [];
        const selectedPlan = this.filterPlan(option);
        const variants = product.variants.filter((item) => item.available);
        const sourceCopy = option.sourceTitles.length ? `For ${option.sourceTitles.join(' + ')}` : 'Replacement filter';
        const includedNotice = this.recommendedBundleIncludesFilter() ? '<small class="setup-filter-subscription__included">Your bundle already includes its first filter where applicable. Choosing an option below adds a separate replacement filter; subscriptions begin with this order and continue at the selected frequency.</small>' : '';
        const buttonLabel = selection.purchase === 'subscription' ? 'Add filter subscription' : (this.recommendedBundleIncludesFilter() ? 'Add another filter' : 'Add filter to setup');
        return `<section class="setup-filter-subscription${selection.added ? ' is-added' : ''}"><div class="setup-filter-subscription__intro"><div class="setup-filter-subscription__media">${(variant.image || product.featuredImage) ? `<img src="${escapeHtml(variant.image || product.featuredImage)}" alt="${escapeHtml(product.title)}" loading="lazy" width="180" height="180">` : ''}</div><div><p>${escapeHtml(this.config.copy.filterEyebrow)}</p><h2>${escapeHtml(product.title)}</h2><span>${escapeHtml(sourceCopy)}</span>${includedNotice}</div></div>${variants.length > 1 ? `<label class="setup-filter-subscription__variant">Filter option<select data-setup-filter-variant data-setup-filter-key="${escapeHtml(option.key)}">${variants.map((item) => `<option value="${item.id}"${item.id === variant.id ? ' selected' : ''}>${escapeHtml(item.title)}</option>`).join('')}</select></label>` : ''}<fieldset><legend>Purchase option</legend><label><input type="radio" name="setup-filter-purchase-${escapeHtml(option.key)}" value="once" data-setup-filter-key="${escapeHtml(option.key)}"${selection.purchase === 'once' ? ' checked' : ''}><span><strong>One-time purchase</strong><small>${escapeHtml(this.money(variant.price))}</small></span></label>${plans.length ? `<label><input type="radio" name="setup-filter-purchase-${escapeHtml(option.key)}" value="subscription" data-setup-filter-key="${escapeHtml(option.key)}"${selection.purchase === 'subscription' ? ' checked' : ''}><span><strong>Subscribe with Appstle</strong><small>${escapeHtml(selectedPlan?.name || 'Automatic delivery')} · ${escapeHtml(this.money(selectedPlan?.price || variant.price))}</small></span></label>` : ''}</fieldset>${plans.length && selection.purchase === 'subscription' && plans.length > 1 ? `<label class="setup-filter-subscription__plan">Delivery frequency<select data-setup-filter-plan data-setup-filter-key="${escapeHtml(option.key)}">${plans.map((plan) => `<option value="${plan.id}"${String(plan.id) === String(selectedPlan?.id) ? ' selected' : ''}>${escapeHtml(plan.name)} — ${escapeHtml(this.money(plan.price))}</option>`).join('')}</select></label>` : ''}<button type="button" class="setup-filter-subscription__toggle" data-setup-action="toggle-filter" data-setup-filter-key="${escapeHtml(option.key)}" aria-pressed="${selection.added}">${selection.added ? '✓ Added to setup' : escapeHtml(buttonLabel)}</button></section>`;
      }).join('');
      return `<div id="SetupFilterSubscription">${cards}</div>`;
    }

    unavailableBundle() {
      if (this.state.mode === 'build-own') return null;
      const match = this.pricingResult().match;
      return match?.offer?.product && match.unavailable ? match.offer.product : null;
    }

    openBundleWaitlist() {
      const product = this.unavailableBundle();
      if (!product?.url) return;
      const match = this.pricingResult().match;
      const destination = new URL(product.url, window.location.origin);
      if (match?.variant?.id) destination.searchParams.set('variant', String(match.variant.id));
      destination.searchParams.set('notify', '1');
      this.close();
      window.location.assign(destination.toString());
    }

    renderSetup(customise) {
      const cards = this.state.useRecommendedBundle ? this.bundleRecommendationCard(customise) : customise
        ? this.state.selectedRooms.map((roomKey) => this.recommendationCard(roomKey, true)).join('')
        : this.recommendationGroups().map((group) => this.recommendationCard(group.roomKeys[0], false, group.roomKeys)).join('');
      const introHeading = !customise && this.state.useRecommendedBundle ? this.bundleDisplayTitle(this.recommendedBundle()) : (customise ? this.config.copy.customiseHeading : this.config.copy.setupHeading);
      const intro = this.renderIntro(`STEP ${customise ? 3 : 2} OF 3`, introHeading, customise ? '' : this.config.copy.setupText);
      const filterJump = this.eligibleForFilter() ? `<a class="setup-builder__filter-jump" href="#SetupFilterSubscription" data-setup-action="view-filter-options">${escapeHtml(this.config.copy.filterJumpLabel || 'Add filter subscriptions')} <span aria-hidden="true">↓</span></a>` : '';
      const filter = customise ? this.renderFilterOwnership() : '';
      const recommendedVariant = this.state.useRecommendedBundle ? this.recommendedBundleVariant() : null;
      const waitlistProduct = this.unavailableBundle();
      const primary = waitlistProduct ? '<button type="button" data-setup-action="join-waitlist">Join the waitlist <span aria-hidden="true">→</span></button>' : customise ? `<button type="button" data-setup-action="add-to-cart" ${(this.state.useRecommendedBundle ? recommendedVariant?.available : this.activeRooms().length) ? '' : 'disabled'}>Add setup to basket <span aria-hidden="true">→</span></button>` : `<button type="button" data-setup-action="customise">${this.state.useRecommendedBundle ? 'Continue' : 'Customise setup'} <span aria-hidden="true">→</span></button>`;
      const pricing = this.pricingResult();
      const productCount = pricing.match && (this.state.useRecommendedBundle || !pricing.match.unavailable) ? this.bundleProductCount(pricing.match.offer, pricing.match.variant) + this.addedFilterCount() : this.setupUnits().length + this.addedFilterCount();
      const mobileSummary = `<div><span>${productCount} ${productCount === 1 ? 'product' : 'products'}</span><strong>${escapeHtml(this.money(this.setupTotal()))}</strong></div>`;
      const stories = this.renderTrustStories();
      const mobileOrderSummary = `<section class="setup-builder__mobile-order-summary">${this.summary()}${filterJump}</section>`;
      return `<div class="setup-builder__workspace"><main>${intro}${this.bundleEditorDiagnostics()}<div class="setup-builder__products">${cards}</div>${customise ? '<button class="setup-builder__add-room" type="button" data-setup-action="add-room"><span>＋</span><strong>Add another room</strong><small>Expand your setup without losing your choices</small></button>' : ''}${stories}${mobileOrderSummary}${filter}</main><aside class="setup-builder__sidebar">${this.summary()}${filterJump}<div class="setup-builder__status" data-setup-status role="status" aria-live="polite"></div>${primary}</aside><footer class="setup-builder__mobile-summary">${mobileSummary}<div class="setup-builder__status" data-setup-status role="status" aria-live="polite"></div>${primary}</footer></div>`;
    }

    async addToCart(button) {
      if (this.unavailableBundle()) { this.openBundleWaitlist(); return; }
      if (button.disabled || (this.state.mode === 'build-own' && this.ownApplianceCount() < 2)) return;
      const bundleId = `setup-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const pricing = this.pricingResult();
      let items = [];
      if (this.state.mode === 'build-own') {
        items = this.ownCartItems(bundleId);
      } else if (pricing.match && !pricing.match.unavailable) {
        items.push({
          id: pricing.match.variant.id,
          quantity: 1,
          properties: {
            _luxen_setup_id: bundleId,
            _luxen_setup_name: 'Build Your Setup',
            _luxen_bundle_offer: pricing.match.offer.label || pricing.match.offer.product.title,
            _setup_entry: this.state.entry.point || 'unknown'
          }
        });
      } else {
        const groupedItems = new Map();
        this.setupUnits().forEach((unit) => {
          const key = `${unit.variant.id}:none`;
          const existing = groupedItems.get(key);
          const roomName = this.room(unit.roomKey)?.title || unit.roomKey;
          const needs = (this.state.needsByRoom[unit.roomKey] || []).map((needKey) => this.need(needKey)?.label).filter(Boolean);
          if (existing) {
            existing.quantity += 1;
            existing.rooms.add(roomName);
            needs.forEach((need) => existing.needs.add(need));
          } else {
            groupedItems.set(key, { id: unit.variant.id, quantity: 1, role: unit.role, rooms: new Set([roomName]), needs: new Set(needs) });
          }
        });
        items = [...groupedItems.values()].map((item) => ({
          id: item.id,
          quantity: item.quantity,
          properties: {
            _luxen_setup_id: bundleId,
            _luxen_setup_name: 'Build Your Setup',
            _setup_rooms: [...item.rooms].join(', '),
            _setup_needs: [...item.needs].join(', '),
            _setup_entry: this.state.entry.point || 'unknown',
            _luxen_appliance_role: item.role,
            ...(item.role === 'fan' || item.role === 'purifier' || item.role === 'vacuum' ? { _luxen_pricing_eligible: 'true' } : {})
          }
        }));
      }
      this.addedFilterOptions().forEach((option) => {
        const selection = this.filterSelection(option);
        const variant = this.filterVariant(option);
        const plan = selection.purchase === 'subscription' ? this.filterPlan(option) : null;
        if (variant) {
          const filterItem = {
            id: variant.id,
            quantity: 1,
            properties: {
              _luxen_setup_id: bundleId,
              _luxen_setup_name: 'Build Your Setup',
              _setup_extra: 'Replacement filter',
              _setup_filter_for: option.sourceTitles.join(', '),
              _setup_entry: this.state.entry.point || 'unknown',
              _luxen_appliance_role: 'filter'
            }
          };
          if (plan?.id) filterItem.selling_plan = Number(plan.id);
          items.push(filterItem);
        }
      });
      if (!items.length) return;
      const buttons = this.querySelectorAll('[data-setup-action="add-to-cart"]');
      const statuses = this.querySelectorAll('[data-setup-status]');
      buttons.forEach((item) => { item.disabled = true; item.textContent = 'Adding setup…'; });
      statuses.forEach((item) => { item.textContent = ''; });
      try {
        const root = window.Shopify?.routes?.root || this.config.routes.root || '/';
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || this.config.copy.errorMessage);
        this.completeAdd();
      } catch (error) {
        let recovered = false;
        if (error instanceof TypeError) {
          try {
            const root = window.Shopify?.routes?.root || this.config.routes.root || '/';
            const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());
            recovered = cart.items?.some((item) => item.properties?._luxen_setup_id === bundleId);
          } catch (cartError) {}
        }
        if (recovered) { this.completeAdd(); return; }
        statuses.forEach((item) => { item.textContent = error.message || this.config.copy.errorMessage; });
        buttons.forEach((item) => { item.disabled = false; item.innerHTML = 'Add setup to basket <span aria-hidden="true">→</span>'; });
      }
    }

    completeAdd() {
      const detail = this.metrics();
      this.emit('setup_add_to_cart', detail);
      this.state = this.initialState();
      this.close();
      document.dispatchEvent(new CustomEvent('cart:open'));
    }
  }

  customElements.define('luxen-setup-builder', LuxenSetupBuilder);
})();
